// EAIRI — what the browser tab, the search result and the link preview say.
//
// PURE. No DOM, no React — so the same function answers for a route rendered in
// a browser and for a route written to static HTML at build time, and the two
// cannot disagree.
//
// Three things were wrong before this existed. index.html carried one title,
// in French, for the whole application: an English reader saw a French tab; a
// person with six tabs open saw six identical ones; and the prerendered
// /tarifs and /methode pages — the ones that exist precisely so LinkedIn and
// Slack can read them without running JavaScript — announced themselves as the
// landing page.

import type { Lang } from "./i18n.ts";

/** The i18n key under `meta.pages` for a path, or null for a route with no
 *  title of its own. Matched on the first segment, so /run/:id and
 *  /verifier/:digest resolve without listing every parameterisation. */
export function pageKey(pathname: string): string | null {
  const seg = pathname.replace(/^\/+/, "").split("/")[0] ?? "";
  if (seg === "") return "landing";
  const known: Record<string, string> = {
    diagnostic: "diagnostic",
    tarifs: "pricing",
    methode: "method",
    livrables: "livrables",
    "a-propos": "apropos",
    contact: "contact",
    compte: "account",
    merci: "account",
    connexion: "signin",
    preuves: "proofs",
    resultat: "pltest",
    trajectoire: "trajectory",
    composite: "composite",
    equipe: "team",
    participer: "participate",
    confirmer: "optin",
    verifier: "verify",
    demarrer: "run",
    run: "run",
    ops: "ops",
    "mentions-legales": "legalNotice",
    cgv: "terms",
    confidentialite: "privacy",
  };
  return known[seg] ?? null;
}

export interface PageMeta {
  readonly title: string;
  readonly description: string;
  readonly lang: Lang;
}

type Translate = (key: string) => string;

/** Resolve the tab title and the meta description for a path.
 *
 *  An unknown route falls back to the application's own name rather than to a
 *  key on screen, which is the failure mode i18n makes easy and which reads as
 *  broken software on a page a buyer is looking at. */
export function pageMeta(t: Translate, pathname: string, lang: Lang): PageMeta {
  const key = pageKey(pathname);
  const appName = t("meta.appName");
  const fallbackDesc = t("meta.description");
  if (!key) return { title: appName, description: fallbackDesc, lang };

  const title = t(`meta.pages.${key}.title`);
  const description = t(`meta.pages.${key}.description`);
  return {
    // The landing page's title is the product's full name and takes no suffix.
    // Every other page is "<page> — Gradixia": the SHORT brand, because a tab
    // truncates at roughly thirty characters and "Tarifs — Gradixia — Indice de
    // prépa…" spends all of them saying the same thing twice.
    title: key === "landing" || title === appName ? appName : `${title} — ${t("meta.brand")}`,
    description: description || fallbackDesc,
    lang,
  };
}

/** Apply it to the live document. Separated from `pageMeta` so the decision
    stays testable without a DOM. */
export function applyPageMeta(doc: Document, meta: PageMeta): void {
  doc.title = meta.title;
  doc.documentElement.lang = meta.lang;
  let tag = doc.querySelector('meta[name="description"]');
  if (!tag) {
    tag = doc.createElement("meta");
    tag.setAttribute("name", "description");
    doc.head.appendChild(tag);
  }
  tag.setAttribute("content", meta.description);
}
